import React, { memo, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Info,
  Loader2,
  Search,
} from 'lucide-react';
import notify from '../../../utils/notify';
import {
  NEW_CLIENT,
  RECORD_ISSUES,
  RECORD_ISSUE_KEYS,
  proposalTax,
  proposalTotals,
  recordRowIssues,
  summarizeRecordRows,
} from './recordImport';

const count = (n) => n.toLocaleString('en-US');
const plural = (n, one, many) => `${count(n)} ${n === 1 ? one : many}`;
const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Excel rows as runs: 4–9, 12.
export function formatRowNumbers(rows) {
  const runs = [];
  rows.forEach((n) => {
    const last = runs[runs.length - 1];
    if (last && n === last[1] + 1) last[1] = n;
    else runs.push([n, n]);
  });
  return runs.map(([a, b]) => (a === b ? String(a) : `${a}–${b}`)).join(', ');
}

// Rows are drawn 100 at a time; a time-tracking history runs to thousands.
const ROWS_PER_PAGE = 100;

// The editable columns of each review table. `input` picks the control.
const COLUMNS = {
  customers: [
    { key: 'name', label: 'Client', wide: true },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'street', label: 'Street' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State', small: true },
    { key: 'zip', label: 'Zip', small: true },
  ],
  vendors: [
    { key: 'name', label: 'Vendor', wide: true },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'link', label: 'Website' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State', small: true },
  ],
  taxLocations: [
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State', small: true },
    { key: 'rate', label: 'Rate %', input: 'number', small: true },
    { key: 'agency', label: 'Agency' },
  ],
  projects: [
    { key: 'name', label: 'Project', wide: true },
    { key: 'clientChoice', label: 'Client', input: 'client' },
    { key: 'taxChoice', label: 'Sales tax', input: 'tax' },
    { key: 'street', label: 'Project address' },
    { key: 'city', label: 'City' },
    { key: 'status', label: 'Status', input: 'status' },
  ],
  rooms: [
    { key: 'projectId', label: 'Project', input: 'project' },
    { key: 'name', label: 'Room', wide: true },
  ],
  // Lines and Total are shown, not edited.
  proposals: [
    { key: 'proposalNumber', label: 'Proposal #' },
    { key: 'projectId', label: 'Project', input: 'project' },
    { key: 'date', label: 'Date', input: 'date' },
    { key: 'title', label: 'Title' },
    { key: 'lines', label: 'Lines', input: 'lines', small: true },
    { key: 'total', label: 'Total', input: 'total' },
    { key: 'status', label: 'Status', input: 'proposalStatus' },
  ],
  timeEntries: [
    { key: 'date', label: 'Date', input: 'date' },
    { key: 'projectId', label: 'Project', input: 'project' },
    { key: 'hours', label: 'Hours', input: 'number', small: true },
    { key: 'rate', label: 'Rate', input: 'number', small: true },
    { key: 'billable', label: 'Billable', input: 'checkbox', small: true },
    { key: 'description', label: 'Description', wide: true },
    { key: 'employee', label: 'Employee' },
  ],
};

// What the bulk bar can set on every row shown.
const BULK_FIELDS = {
  customers: [
    { key: 'state', label: 'State' },
    { key: 'city', label: 'City' },
  ],
  vendors: [{ key: 'state', label: 'State' }],
  taxLocations: [
    { key: 'agency', label: 'Agency' },
    { key: 'state', label: 'State' },
  ],
  projects: [
    { key: 'clientChoice', label: 'Client', input: 'client' },
    { key: 'taxChoice', label: 'Sales tax', input: 'tax' },
    { key: 'status', label: 'Status', input: 'status' },
  ],
  rooms: [{ key: 'projectId', label: 'Project', input: 'project' }],
  proposals: [
    { key: 'status', label: 'Status', input: 'proposalStatus' },
    { key: 'projectId', label: 'Project', input: 'project' },
  ],
  timeEntries: [
    { key: 'employee', label: 'Employee' },
    { key: 'activity', label: 'Activity' },
    { key: 'projectId', label: 'Project', input: 'project' },
    { key: 'rate', label: 'Rate', input: 'number' },
  ],
};

const columnClass = (col) => (col.small ? 'pi-col-small' : col.wide ? 'pi-col-wide' : '');

const CHOICE_INPUTS = ['client', 'tax', 'project', 'status', 'proposalStatus'];
// What a choice in the bulk bar starts at.
const BULK_DEFAULTS = { status: 'active', proposalStatus: 'archived' };

// The column a required problem is shown on.
const ISSUE_COLUMN = {
  name: 'name',
  project: 'projectId',
  date: 'date',
  hours: 'hours',
  rate: 'rate',
  place: 'city',
  tooBig: 'lines',
};

const byLabel = (a, b) => a.label.localeCompare(b.label);

function useChoices(context) {
  return useMemo(() => {
    const customers = (context?.customers || [])
      .map((c) => ({ value: c.customerId, label: c.email ? `${c.name} (${c.email})` : c.name }))
      .sort(byLabel);
    const taxes = (context?.taxLocations || []).map((t) => ({
      value: t.id,
      label: `${[t.city, t.state].filter(Boolean).join(', ')} · ${t.tax}`,
    }));
    const projects = (context?.projects || [])
      .map((p) => ({
        value: p.projectId,
        label: `${p.name || p.customerName || 'Untitled Project'}${p.isActive === false ? ' (inactive)' : ''}`,
      }))
      .sort(byLabel);
    return { customers, taxes, projects };
  }, [context]);
}

function ChoiceSelect({ input, value, onChange, choices, row, label, id, invalid }) {
  let options;
  if (input === 'client') {
    options = [
      { value: '', label: 'No client' },
      ...(row?.extra.clientName
        ? [{ value: NEW_CLIENT, label: `New client: ${row.extra.clientName}` }]
        : []),
      ...(!row ? [{ value: NEW_CLIENT, label: "New client from each project's details" }] : []),
      ...choices.customers,
    ];
  } else if (input === 'tax') {
    options = [{ value: '', label: 'No sales tax' }, ...choices.taxes];
  } else if (input === 'project') {
    options = [{ value: '', label: 'Choose a project' }, ...choices.projects];
  } else if (input === 'proposalStatus') {
    options = [
      { value: 'archived', label: 'Archived (for reference)' },
      { value: 'draft', label: 'Draft (still being worked on)' },
    ];
  } else {
    options = [
      { value: 'active', label: 'Active' },
      { value: 'archived', label: 'Inactive (archived)' },
    ];
  }
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      aria-invalid={invalid || undefined}
      className={invalid ? 'has-error' : ''}
    >
      {options.map((option) => (
        <option key={option.value || 'none'} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

// A proposal's total at the rate it will be saved with, and the old system's
// when that differs.
function ProposalTotal({ row, context, sourceName }) {
  const { total } = proposalTotals(row.extra.lines, proposalTax(row, context).rate);
  const source = row.extra.sourceTotal;
  return (
    <div className="pi-total-cell">
      <span>{money(total)}</span>
      {source !== null && Math.abs(total - source) > 1 && (
        <span className="pi-total-source">
          {sourceName}: {money(source)}
        </span>
      )}
    </div>
  );
}

// A proposal's lines as they'll be saved.
function ProposalLines({ id, lines }) {
  const hasRoom = lines.some((l) => l.room);
  const hasVendor = lines.some((l) => l.vendorName);
  return (
    <div id={id} className="pi-lines-wrap">
      <table className="pi-lines-table">
        <thead>
          <tr>
            {hasRoom && <th>Room</th>}
            <th>Item</th>
            {hasVendor && <th>Vendor</th>}
            <th className="is-num">Qty</th>
            <th className="is-num">Cost</th>
            <th className="is-num">Markup</th>
            <th className="is-num">Unit price</th>
            <th className="is-num">Shipping</th>
            <th className="is-num">Line total</th>
            <th>Taxed</th>
          </tr>
        </thead>
        <tbody>
          {/* Lines have no id until they're saved, and this list never reorders. */}
          {lines.map((l, index) => (
            <tr key={index}>
              {hasRoom && <td>{l.room}</td>}
              <td>
                {l.clientName || l.name}
                {l.description && <div className="pi-line-description">{l.description}</div>}
              </td>
              {hasVendor && <td>{l.vendorName}</td>}
              <td className="is-num">{count(l.qty)}</td>
              <td className="is-num">{money(l.tradePrice)}</td>
              <td className="is-num">{`${Number(l.markup.toFixed(2))}%`}</td>
              <td className="is-num">{money(l.clientUnit)}</td>
              <td className="is-num">{l.clientShipping ? money(l.clientShipping) : '—'}</td>
              <td className="is-num">
                {money(Number((l.clientUnit * l.qty).toFixed(2)) + l.clientShipping)}
              </td>
              <td>
                {l.taxable ? 'Yes' : 'No'}
                {l.shippingTaxable ? ', with shipping' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One table row. Memoized so typing in one row doesn't redraw the others.
const RecordRow = memo(function RecordRow({
  type,
  row,
  columns,
  choices,
  context,
  sourceName,
  onUpdateRow,
  onSelectRows,
  label,
}) {
  const [linesOpen, setLinesOpen] = useState(false);
  const setField = (key, value) => onUpdateRow(row.id, { fields: { [key]: value } });
  const errorColumns = new Set(
    row.selected
      ? recordRowIssues(type, row)
          .filter((key) => RECORD_ISSUES[key].blocking)
          .map((key) => ISSUE_COLUMN[key])
      : []
  );
  // Shown under the row's first field, the way the product review shows them.
  // A proposal is always several rows, so that goes without saying.
  const notes = [
    row.existing && { text: row.existing, tone: 'muted' },
    row.archived && !row.existing && { text: 'Archived in the old system', tone: 'muted' },
    row.sourceRows.length > 1 &&
      type !== 'proposals' && {
        text: `Combined from ${row.sourceRows.length} rows`,
        tone: 'info',
      },
  ].filter(Boolean);
  const linesId = `pi-lines-${row.id}`;

  const main = (
    <tr className={row.selected ? '' : 'is-unselected'}>
      <td className="pi-col-check">
        <input
          id={`pi-select-${row.id}`}
          type="checkbox"
          checked={row.selected}
          onChange={(e) => onSelectRows([row.id], e.target.checked)}
          aria-label={`Import ${label || 'this row'}`}
        />
      </td>
      <td className="pi-col-rows pi-muted">{formatRowNumbers(row.sourceRows)}</td>
      {columns.map((col, index) => {
        const id = `pi-${col.key}-${row.id}`;
        const invalid = errorColumns.has(col.key);
        const value = row.fields[col.key];
        let control;
        if (col.input === 'checkbox') {
          control = (
            <input
              id={id}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => setField(col.key, e.target.checked)}
              aria-label={col.label}
            />
          );
        } else if (col.input === 'lines') {
          const n = row.extra.lines.length;
          control = (
            <button
              id={id}
              type="button"
              className={`pi-lines-toggle${invalid ? ' has-error' : ''}`}
              onClick={() => setLinesOpen((open) => !open)}
              aria-expanded={linesOpen}
              aria-controls={linesOpen ? linesId : undefined}
              aria-label={`${linesOpen ? 'Hide' : 'Show'} the ${plural(n, 'line', 'lines')} of ${label}`}
            >
              {linesOpen ? (
                <ChevronDown size={14} aria-hidden="true" />
              ) : (
                <ChevronRight size={14} aria-hidden="true" />
              )}
              {count(n)}
            </button>
          );
        } else if (col.input === 'total') {
          control = <ProposalTotal row={row} context={context} sourceName={sourceName} />;
        } else if (CHOICE_INPUTS.includes(col.input)) {
          control = (
            <ChoiceSelect
              id={id}
              input={col.input}
              value={value}
              onChange={(next) => setField(col.key, next)}
              choices={choices}
              row={row}
              label={col.label}
              invalid={invalid}
            />
          );
        } else {
          control = (
            <input
              id={id}
              type={col.input === 'number' ? 'number' : col.input === 'date' ? 'date' : 'text'}
              step={col.input === 'number' ? 'any' : undefined}
              value={value}
              onChange={(e) => setField(col.key, e.target.value)}
              aria-label={col.label}
              aria-invalid={invalid || undefined}
              className={invalid ? 'has-error' : ''}
            />
          );
        }
        return (
          <td key={col.key} className={columnClass(col)}>
            {control}
            {index === 0 && notes.length > 0 && (
              <div className="pi-row-notes">
                {notes.map((note) => (
                  <span key={note.text} className={`pi-chip pi-chip--${note.tone}`}>
                    {note.text}
                  </span>
                ))}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
  if (!linesOpen) return main;
  return (
    <>
      {main}
      <tr className={`pi-lines-row${row.selected ? '' : ' is-unselected'}`}>
        <td colSpan={columns.length + 2}>
          <ProposalLines id={linesId} lines={row.extra.lines} />
        </td>
      </tr>
    </>
  );
});

function RecordIssues({ type, summary, noun, nouns, activeKey, onToggle, onLeaveOut }) {
  const keys = RECORD_ISSUE_KEYS[type].filter((key) => summary.issues[key] > 0);
  const describe = (key) => {
    const n = summary.issues[key];
    return `${plural(n, noun, nouns)} ${RECORD_ISSUES[key].phrase[n === 1 ? 0 : 1]}`;
  };
  return (
    <section className="pi-card pi-issues" aria-labelledby="pi-issues-title">
      <div className="pi-issues-head">
        <h3 id="pi-issues-title">Needs attention</h3>
        <p>Required items block the import. The rest are worth a look before it.</p>
      </div>
      {summary.selected === 0 ? (
        <p className="pi-issues-clear is-muted">Nothing is selected to import.</p>
      ) : keys.length === 0 ? (
        <p className="pi-issues-clear">
          <CheckCircle2 size={18} aria-hidden="true" /> Everything looks right. Ready to import.
        </p>
      ) : (
        <ul className="pi-issue-list">
          {keys.map((key) => {
            const { blocking } = RECORD_ISSUES[key];
            const isActive = activeKey === key;
            return (
              <li
                key={key}
                className={`pi-issue ${blocking ? 'is-error' : 'is-warning'}${isActive ? ' is-active' : ''}`}
              >
                {blocking ? (
                  <AlertCircle size={18} aria-hidden="true" />
                ) : (
                  <AlertTriangle size={18} aria-hidden="true" />
                )}
                <span className="pi-issue-text">{describe(key)}</span>
                {blocking && <span className="pi-chip pi-chip--error">Required</span>}
                <span className="pi-issue-actions">
                  {blocking && (
                    <button
                      type="button"
                      className="pi-link"
                      aria-label={`Leave these out: ${describe(key)}`}
                      onClick={() => onLeaveOut(key)}
                    >
                      Leave these out
                    </button>
                  )}
                  <button
                    type="button"
                    className="pi-btn pi-btn--outline"
                    aria-label={isActive ? 'Show all rows' : `Show these: ${describe(key)}`}
                    onClick={() => onToggle(key)}
                  >
                    {isActive ? 'Show all' : 'Show these'}
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function RecordReviewStep({
  type,
  noun,
  nouns,
  fileName,
  sheetName,
  rows,
  rowStats,
  context,
  labelFor,
  onUpdateRow,
  onSelectRows,
  onApplyToRows,
  billed,
  onChangeBilled,
  billedLabel,
  sourceName,
  onBack,
  onImport,
  importing,
}) {
  const columns = COLUMNS[type];
  const bulkFields = BULK_FIELDS[type];
  const choices = useChoices(context);
  const [query, setQuery] = useState('');
  const [rowLimit, setRowLimit] = useState(ROWS_PER_PAGE);
  // An issue's rows as they were when "Show these" was clicked, so fixing one
  // doesn't drop it out of the table mid-edit.
  const [issueFilter, setIssueFilter] = useState(null);
  const [bulkKey, setBulkKey] = useState(bulkFields[0].key);
  const [bulkValue, setBulkValue] = useState(BULK_DEFAULTS[bulkFields[0].input] ?? '');

  const summary = useMemo(() => summarizeRecordRows(type, rows), [type, rows]);
  const bulk = bulkFields.find((field) => field.key === bulkKey) || bulkFields[0];

  const visibleRows = useMemo(() => {
    const inIssue = issueFilter ? rows.filter((row) => issueFilter.ids.has(row.id)) : rows;
    const q = query.trim().toLowerCase();
    if (!q) return inIssue;
    return inIssue.filter((row) =>
      [...Object.values(row.fields), ...Object.values(row.extra)]
        .filter((value) => typeof value === 'string')
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query, issueFilter]);
  const renderedRows = visibleRows.slice(0, rowLimit);
  const hiddenCount = visibleRows.length - renderedRows.length;
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((row) => row.selected);

  const toggleIssue = (key) => {
    setRowLimit(ROWS_PER_PAGE);
    if (issueFilter?.key === key) {
      setIssueFilter(null);
      return;
    }
    const ids = new Set(
      rows
        .filter((row) => row.selected && recordRowIssues(type, row).includes(key))
        .map((row) => row.id)
    );
    setIssueFilter({ key, ids });
    setQuery('');
  };

  const leaveOut = (key) => {
    const ids = rows
      .filter((row) => row.selected && recordRowIssues(type, row).includes(key))
      .map((row) => row.id);
    if (!ids.length) return;
    onSelectRows(ids, false);
    if (issueFilter?.key === key) setIssueFilter(null);
    notify.success(`Left out ${plural(ids.length, noun, nouns)}. Tick them again to import them.`);
  };

  const q = query.trim();
  let bulkScope = `all ${plural(visibleRows.length, noun, nouns)}`;
  if (q) bulkScope = `the ${plural(visibleRows.length, noun, nouns)} matching “${q}”`;
  else if (issueFilter) bulkScope = `the ${plural(visibleRows.length, noun, nouns)} shown`;
  const isChoice = CHOICE_INPUTS.includes(bulk.input);
  const bulkReady = visibleRows.length > 0 && (isChoice || bulkValue.trim() !== '');

  const applyBulk = () => {
    if (!bulkReady) return;
    const value =
      bulk.input === 'number' ? String(Number(bulkValue)) : isChoice ? bulkValue : bulkValue.trim();
    onApplyToRows(
      visibleRows.map((row) => row.id),
      bulk.key,
      value
    );
    notify.success(
      `Set the ${bulk.label.toLowerCase()} on ${plural(visibleRows.length, noun, nouns)}.`
    );
    setBulkValue(BULK_DEFAULTS[bulk.input] ?? '');
  };

  const needsBilledDate = type === 'timeEntries' && billed.mode === 'before' && !billed.before;
  const blocked = importing || summary.selected === 0 || summary.blocking > 0 || needsBilledDate;

  const notes = [];
  const household = rowStats.householdMerges || 0;
  const repeated = rowStats.combinedRows - household;
  if (household > 0) {
    notes.push(
      `${plural(household, 'person was', 'people were')} added to their household's client. The first is its secondary contact, and everyone is listed in its notes.`
    );
  }
  if (repeated > 0) {
    notes.push(
      type === 'projects'
        ? `${plural(repeated, 'repeated row was', 'repeated rows were')} combined. Ivy ties time and rooms to a project by its name alone, so same-name projects for one client become one; any other address is kept in its notes.`
        : `${plural(repeated, 'repeated row was', 'repeated rows were')} combined with the ${noun} ${repeated === 1 ? 'it repeats' : 'they repeat'}.`
    );
  }
  if (rowStats.otherRoleRows > 0) {
    notes.push(
      type === 'customers'
        ? `${plural(rowStats.otherRoleRows, 'row labelled as a vendor was', 'rows labelled as vendors were')} left out. Import the file as Vendors for those.`
        : `${plural(rowStats.otherRoleRows, 'row not labelled as a vendor was', 'rows not labelled as vendors were')} left out.`
    );
  }
  if (rowStats.missingName > 0) {
    notes.push(
      `${plural(rowStats.missingName, 'row has', 'rows have')} no name and ${rowStats.missingName === 1 ? 'was' : 'were'} left out.`
    );
  }
  if (rowStats.existing > 0) {
    notes.push(
      `${plural(rowStats.existing, `${noun} is`, `${nouns} are`)} already in this studio and left unselected.`
    );
  }
  if (rowStats.archivedUnselected > 0) {
    notes.push(
      `${plural(rowStats.archivedUnselected, `archived ${noun} is`, `archived ${nouns} are`)} left unselected.`
    );
  }
  if (rowStats.archivedIncluded > 0) {
    notes.push(
      type === 'projects'
        ? `${plural(rowStats.archivedIncluded, 'archived project comes', 'archived projects come')} in as inactive, so their time and rooms have somewhere to go.`
        : `${plural(rowStats.archivedIncluded, 'archived client is', 'archived clients are')} included so their projects can link to them.`
    );
  }
  if (rowStats.declined > 0) {
    notes.push(
      `${plural(rowStats.declined, 'line the client declined was', 'lines the client declined were')} left out.`
    );
  }
  if (rowStats.notOnProposal > 0) {
    notes.push(
      `${plural(rowStats.notOnProposal, 'row only on a purchase order or invoice was', 'rows only on a purchase order or invoice were')} left out.`
    );
  }
  if (rowStats.purchaseOnly > 0) {
    notes.push(
      `${plural(rowStats.purchaseOnly, 'row with a cost but no selling price was', 'rows with a cost but no selling price were')} left out as purchase order lines.`
    );
  }
  if (rowStats.placeholderEmails > 0) {
    notes.push(
      `${plural(rowStats.placeholderEmails, 'stand-in email', 'stand-in emails')} like no@email.com ${rowStats.placeholderEmails === 1 ? 'was' : 'were'} left blank.`
    );
  }

  return (
    <>
      <div className="pi-card pi-file-chip">
        <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
        <span>{fileName}</span>
        {sheetName && <span className="pi-chip pi-chip--info">{sheetName}</span>}
      </div>

      <RecordIssues
        type={type}
        summary={summary}
        noun={noun}
        nouns={nouns}
        activeKey={issueFilter?.key || ''}
        onToggle={toggleIssue}
        onLeaveOut={leaveOut}
      />

      {type === 'timeEntries' && (
        <section className="pi-card pi-options-card">
          <label className="pi-field-label" htmlFor="pi-billed-mode">
            Was this time already billed?
          </label>
          <div className="pi-options-row">
            <select
              id="pi-billed-mode"
              value={billed.mode}
              onChange={(e) => onChangeBilled({ ...billed, mode: e.target.value })}
            >
              <option value="all">Yes, all of it was billed in {sourceName}</option>
              <option value="before">Only the time before a date</option>
              <option value="none">No, none of it was billed</option>
            </select>
            {billed.mode === 'before' && (
              <input
                type="date"
                value={billed.before}
                onChange={(e) => onChangeBilled({ ...billed, before: e.target.value })}
                aria-label="Billed before"
              />
            )}
          </div>
          <p className="pi-help">
            {billed.mode === 'none'
              ? 'All of it can go on a time billing invoice in InteriorFolio.'
              : `Billed time shows as “${billedLabel}” and is locked, so it can't go on an invoice again. ${sourceName === 'Ivy' ? "Ivy's export doesn't say which time was billed." : ''}`}
          </p>
        </section>
      )}

      <section className="pi-card pi-review-card">
        <div className="pi-review-head">
          <strong>{plural(summary.selected, `${noun} selected`, `${nouns} selected`)}</strong>
          <label className="pi-search">
            <Search size={16} aria-hidden="true" />
            <input
              id="pi-review-search"
              type="search"
              placeholder={`Search ${nouns}...`}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setRowLimit(ROWS_PER_PAGE);
              }}
              aria-label={`Search ${nouns}`}
            />
          </label>
        </div>

        <div className="pi-bulk">
          <span className="pi-bulk-label">Set</span>
          <select
            id="pi-bulk-field"
            className="pi-bulk-field"
            value={bulk.key}
            onChange={(e) => {
              const next = bulkFields.find((field) => field.key === e.target.value);
              setBulkKey(e.target.value);
              setBulkValue(BULK_DEFAULTS[next?.input] ?? '');
            }}
            aria-label="Field to set"
          >
            {bulkFields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
          <span className="pi-bulk-label">for {bulkScope}</span>
          {isChoice ? (
            <ChoiceSelect
              id="pi-bulk-value"
              input={bulk.input}
              value={bulkValue}
              onChange={setBulkValue}
              choices={choices}
              label={`Set the ${bulk.label.toLowerCase()} for ${bulkScope}`}
            />
          ) : (
            <input
              id="pi-bulk-value"
              type={bulk.input === 'number' ? 'number' : 'text'}
              step={bulk.input === 'number' ? 'any' : undefined}
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              aria-label={`Set the ${bulk.label.toLowerCase()} for ${bulkScope}`}
            />
          )}
          <button
            type="button"
            className="pi-btn pi-btn--outline-accent"
            onClick={applyBulk}
            disabled={!bulkReady}
          >
            Apply
          </button>
        </div>

        {issueFilter && (
          <div className="pi-filter-note" role="status">
            <span>
              Showing {plural(issueFilter.ids.size, noun, nouns)} from “
              {RECORD_ISSUES[issueFilter.key].title}”.
            </span>
            <button type="button" className="pi-link" onClick={() => setIssueFilter(null)}>
              Show all {nouns}
            </button>
          </div>
        )}

        <div className="pi-table-wrap pi-review-table-wrap">
          <table className="pi-table pi-record-table">
            <thead>
              <tr>
                <th className="pi-col-check">
                  <input
                    id="pi-select-all"
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) =>
                      onSelectRows(
                        visibleRows.map((row) => row.id),
                        e.target.checked
                      )
                    }
                    aria-label={`Select all ${nouns}`}
                  />
                </th>
                <th className="pi-col-rows">Excel row</th>
                {columns.map((col) => (
                  <th key={col.key} className={columnClass(col)}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderedRows.map((row) => (
                <RecordRow
                  key={row.id}
                  type={type}
                  row={row}
                  columns={columns}
                  choices={choices}
                  context={context}
                  sourceName={sourceName}
                  onUpdateRow={onUpdateRow}
                  onSelectRows={onSelectRows}
                  label={labelFor(row)}
                />
              ))}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 2} className="pi-empty">
                    No {nouns} match &ldquo;{query}&rdquo;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {hiddenCount > 0 && (
          <div className="pi-more-rows">
            <span>
              Showing {count(renderedRows.length)} of {count(visibleRows.length)}. Search to find a{' '}
              {noun} faster.
            </span>
            <button
              type="button"
              className="pi-btn pi-btn--outline"
              onClick={() => setRowLimit((limit) => limit + ROWS_PER_PAGE)}
            >
              Show {Math.min(ROWS_PER_PAGE, hiddenCount)} more
            </button>
          </div>
        )}
      </section>

      {notes.length > 0 && (
        <aside className="pi-card pi-summary pi-record-summary">
          <h3>Import summary</h3>
          <div className="pi-summary-notes">
            {notes.map((note) => (
              <p key={note}>
                <Info size={16} aria-hidden="true" />
                <span>{note}</span>
              </p>
            ))}
          </div>
        </aside>
      )}

      <div className="pi-footer">
        <button
          type="button"
          className="pi-btn pi-btn--outline"
          onClick={onBack}
          disabled={Boolean(importing)}
        >
          <ArrowLeft size={16} aria-hidden="true" /> Back to Map Columns
        </button>
        <div className="pi-footer-actions">
          <div className="pi-import-action">
            <button
              type="button"
              className="pi-btn pi-btn--primary pi-btn--lg"
              onClick={onImport}
              disabled={blocked}
            >
              {importing ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" />
                  Importing {count(importing.done)} of {count(importing.total)}…
                </>
              ) : (
                <>
                  Import {plural(summary.selected, noun, nouns)}{' '}
                  <ArrowRight size={16} aria-hidden="true" />
                </>
              )}
            </button>
            {!importing && summary.blocking > 0 && (
              <span className="pi-footer-hint">
                Fix or leave out the required items under Needs attention to import.
              </span>
            )}
            {!importing && summary.blocking === 0 && needsBilledDate && (
              <span className="pi-footer-hint">Choose the date billing stopped.</span>
            )}
            {!importing && summary.blocking === 0 && summary.selected === 0 && (
              <span className="pi-footer-hint">Select at least one {noun} to import.</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
